// Encabezado del archivo (imports al nivel superior)
import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card';
import { TrendingDown, Users, FileText, CreditCard, Award, ArrowDown, AlertCircle } from 'lucide-react';
import designTokens from '../../../styles/tokens';

/**
 * Componente de gráfico de embudo de conversión
 * Muestra el flujo de conversión desde sesiones hasta certificados
 */
function ConversionFunnelChart({ 
  data, 
  source,
  companyId,
  isLoading = false,
  className = "",
  showPercentages = true,
  colorScheme = 'blue',
  timeframe = '30d'
}) {
    // Definir colores dentro del componente
    const colors = {
      blue: ['#3b82f6', '#60a5fa', '#93c5fd', '#bfdbfe'],
      green: ['#158B5C', '#34d399', '#6ee7b7', '#a7f3d0'],
      purple: ['#8b5cf6', '#a78bfa', '#c4b5fd', '#ddd6fe']
    };
    const currentColors = colors[colorScheme] || colors.blue;

    // NUEVO: usar tokens de charts.funnel (sin imports dentro de la función)
    const funnelTokens = designTokens?.charts?.funnel || {};
    const stageColors = funnelTokens.colors?.stages || ['#3b82f6', '#a855f7', '#f43f5e', '#158B5C', '#f59e0b'];
    const barBg = funnelTokens.colors?.barBg || 'rgba(255,255,255,0.08)';
    const textMuted = funnelTokens.colors?.textMuted || '#9CA3AF';
    const barHeight = funnelTokens.sizes?.barHeight || 16;
    const barRadius = funnelTokens.sizes?.radius || 10;
    const improvementPerUnit = funnelTokens.metrics?.improvementPerUnit || 100000;


  const icons = {
    'Sesiones': Users,
    'Cotizaciones': FileText,
    'Contrataciones': CreditCard,
    'Certificados': Award
  };

  // Función para obtener el texto del período
  const getPeriodText = (timeframe) => {
    const days = parseInt(timeframe.replace('d', ''));
    if (days === 1) return 'Hoy';
    if (days === 7) return 'Últimos 7 días';
    if (days === 30) return 'Últimos 30 días';
    if (days === 90) return 'Últimos 90 días';
    return `Últimos ${days} días`;
  };

  const formatData = (funnelData) => {
    if (!funnelData) return [];

    return [
      {
        name: 'Sesiones',
        value: funnelData.sessions?.count || 0,
        percentage: 100,
        icon: 'Users'
      },
      {
        name: 'Cotizaciones',
        value: funnelData.quotations?.count || 0,
        percentage: funnelData.quotations?.percentage || 0,
        icon: 'FileText'
      },
      {
        name: 'Contrataciones',
        value: funnelData.contractions?.count || 0,
        percentage: funnelData.contractions?.percentage || 0,
        icon: 'CreditCard'
      },
      {
        name: 'Suscripciones',
        value: funnelData.subscriptions?.count || 0,
        percentage: funnelData.subscriptions?.percentage || 0,
        icon: 'CreditCard'
      },
      {
        name: 'Certificados',
        value: funnelData.certificates?.count || 0,
        percentage: funnelData.certificates?.percentage || 0,
        icon: 'Award'
      }
    ];
  };

  const computeFromSource = () => {
    if (!source || !companyId) return null;
    const cot = Array.isArray(source.cotizaciones) ? source.cotizaciones : [];
    const con = Array.isArray(source.contrataciones) ? source.contrataciones : [];
    const sus = Array.isArray(source.suscripciones) ? source.suscripciones : [];
    const cer = Array.isArray(source.certificados) ? source.certificados : [];
    const companyEq = v => String(v || '').trim() === String(companyId).trim();
    const cotByCompany = cot.filter(r => companyEq(r.compania_id) || companyEq(r.company_id));
    const conByCompany = con.filter(r => companyEq(r.compania_id) || companyEq(r.company_id));
    const susByCompany = sus.filter(r => companyEq(r.company_id) || companyEq(r.compania_id));
    const cerByCompany = cer.filter(r => companyEq(r.compania_id) || companyEq(r.company_id));
    const sessionsCount = new Set(cotByCompany.map(r => r.sesion_id).filter(Boolean)).size;
    const quotationsCount = cotByCompany.length;
    const contractionsCount = conByCompany.length;
    const subscriptionsCount = susByCompany.length;
    const certificatesCount = cerByCompany.length;
    const mrrUF = susByCompany.reduce((sum, s) => {
      const status = String(s?.status || '').toLowerCase();
      const isActive = status === 'confirmada' || status === 'activa';
      if (!isActive) return sum;
      const amount = Number(String(s?.amount || '0').replace(',', '.')) || 0;
      const currency = String(s?.currency || '').toUpperCase();
      if (currency === 'UF') return sum + amount;
      return sum;
    }, 0);
    const arrUF = mrrUF * 12;
    const stages = [
      { key: 'sessions', count: sessionsCount },
      { key: 'quotations', count: quotationsCount },
      { key: 'contractions', count: contractionsCount },
      { key: 'subscriptions', count: subscriptionsCount },
      { key: 'certificates', count: certificatesCount }
    ];
    const withPercent = stages.map((s, idx) => {
      const prev = idx === 0 ? s.count || 1 : stages[idx - 1].count || 0;
      const pct = idx === 0 ? 100 : prev > 0 ? (s.count / prev) * 100 : 0;
      return { key: s.key, count: s.count, percentage: pct };
    });
    return {
      sessions: { count: withPercent[0].count, percentage: withPercent[0].percentage },
      quotations: { count: withPercent[1].count, percentage: withPercent[1].percentage },
      contractions: { count: withPercent[2].count, percentage: withPercent[2].percentage },
      subscriptions: { count: withPercent[3].count, percentage: withPercent[3].percentage },
      certificates: { count: withPercent[4].count, percentage: withPercent[4].percentage },
      mrrUF,
      arrUF
    };
  };

  const computed = data ? data : computeFromSource();
  const chartData = formatData(computed || {});

  // NUEVO: métricas de resumen
  const maxValue = Math.max(1, ...(chartData.map(i => i.value)));
  const endToEndRate = chartData.length >= 2 ? (chartData[chartData.length - 1].value / chartData[0].value) * 100 : 0;
  const drops = chartData.map((item, idx) => {
    if (idx === 0) return { stage: item.name, dropPercent: 0, dropCount: 0 };
    const prev = chartData[idx - 1];
    const dropPercent = 100 - (item.percentage || 0);
    const dropCount = Math.max(0, (prev.value || 0) - (item.value || 0));
    return { stage: item.name, dropPercent, dropCount };
  });
  const biggestDrop = drops.slice(1).reduce((acc, cur) => cur.dropPercent > acc.dropPercent ? cur : acc, { stage: '', dropPercent: 0, dropCount: 0 });

  const renderFunnelShape = () => {
    if (!chartData || chartData.length === 0) return null;

    return (
      <div className="space-y-4">
        {chartData.map((item, index) => {
          const widthPercentage = Math.max(6, (item.value / maxValue) * 100);
          const isLast = index === chartData.length - 1;

          return (
            <div key={item.name} className="space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-foreground">{item.name}</div>
                <div className="text-sm font-semibold text-foreground">{item.value.toLocaleString()}</div>
              </div>

              {/* Barra horizontal: fondo + progreso */}
              <div
                className="w-full rounded-lg"
                style={{
                  height: `${barHeight}px`,
                  borderRadius: `${barRadius}px`,
                  backgroundColor: barBg
                }}
              >
                <div
                  className="h-full rounded-lg transition-all"
                  style={{
                    width: `${widthPercentage}%`,
                    borderRadius: `${barRadius}px`,
                    backgroundColor: stageColors[index]
                  }}
                />
              </div>

              {/* Dropoff mostrado como en el ejemplo */}
              {index > 0 && (
                <div className="flex items-center justify-end text-xs" style={{ color: textMuted }}>
                  ({(100 - (item.percentage || 0)).toFixed(1)}% caída)
                </div>
              )}

              {!isLast && (
                <div className="flex items-center justify-center">
                  <ArrowDown className="h-4 w-4" style={{ color: textMuted }} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderFunnelStats = () => {
    if (!chartData || chartData.length === 0) return null;

    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        {chartData.map((item, index) => {
          const Icon = icons[item.name];
          return (
            <div key={item.name} className="flex items-center space-x-3 p-3 bg-muted/50 rounded-lg">
              <div className="flex-shrink-0">
                <div 
                  className="w-10 h-10 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: `${currentColors[index]}20` }}
                >
                  <Icon 
                    className="h-5 w-5" 
                    style={{ color: currentColors[index] }}
                  />
                </div>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">
                  {item.name}
                </p>
                <p className="text-lg font-bold text-foreground">
                  {item.value.toLocaleString()}
                </p>
                {showPercentages && index > 0 && (
                  <p className="text-xs text-muted-foreground">
                    {item.percentage.toFixed(1)}% conversión
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingDown className="h-5 w-5" />
            <span>Embudo de Conversión</span>
          </CardTitle>
          <CardDescription>
            Flujo de conversión desde sesiones hasta certificados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <TrendingDown className="h-5 w-5" />
            <span>Embudo de Conversión</span>
          </CardTitle>
          <CardDescription>
            Flujo de conversión desde sesiones hasta certificados
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-muted-foreground">
            No hay datos disponibles
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <TrendingDown className="h-5 w-5" />
            <span>Embudo de Conversión</span>
          </div>
          <div className="text-sm font-normal text-muted-foreground bg-muted px-3 py-1 rounded-full">
            {getPeriodText(timeframe)}
          </div>
        </CardTitle>
        <CardDescription>
          Visualiza el progreso a través de procesos clave del negocio
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {/* Embudo visual */}
          <div className="py-2">
            {renderFunnelShape()}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">Conversion Rate</div>
              <div className="text-2xl font-bold">{endToEndRate.toFixed(2)}%</div>
              <div className="text-xs text-muted-foreground">End-to-end conversion</div>
            </div>

            <div className="rounded-lg border bg-card p-4">
              <div className="text-xs text-muted-foreground mb-1">Biggest Dropoff</div>
              <div className="text-lg font-semibold">{biggestDrop.stage} ({biggestDrop.dropPercent.toFixed(1)}%)</div>
              <div className="text-xs text-muted-foreground">Etapa con mayor abandono</div>
            </div>

            {/* Removido bloque de oportunidad de mejora para claridad */}
          </div>
          {computed && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">MRR (UF)</div>
                <div className="text-2xl font-bold">{(computed.mrrUF || 0).toFixed(2)} UF</div>
              </div>
              <div className="rounded-lg border bg-card p-4">
                <div className="text-xs text-muted-foreground mb-1">ARR (UF)</div>
                <div className="text-2xl font-bold">{(computed.arrUF || 0).toFixed(2)} UF</div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default ConversionFunnelChart;
